/**
 * CmsHomeService
 *
 * Adapter for the new BKFC CMS API's home/rail endpoints
 * (apps/api/src/routers/dsp-replacement.ts in bkfc-cms):
 *
 *   GET /v1/home/:slug  — personalized home screen (rails resolved per
 *                          viewer entitlement/tier/geo/cohort). Never cached.
 *   GET /app/load       — anonymous bootstrap payload (legacy-shaped
 *                          `homepage` array + the same `rails` the home
 *                          endpoint returns).
 *
 * This module's job is purely translation: map the CMS response into the
 * SAME shape AppLoadContext / CategoryRail / ChannelCard already consume
 * from SpotlightService.getAppLoad() today (see
 * src/components/sample_appload_response.json for the reference shape and
 * src/contexts/AppLoadContext.js / src/components/ChannelCard.js for the
 * fields actually read off each channel/category).
 *
 * ---------------------------------------------------------------------
 * FIELD MAPPING — CATEGORY (CMS `ResolvedRail` -> legacy `category`)
 * ---------------------------------------------------------------------
 *   rail.id                  -> category._id, category.slug
 *                                (CMS rails have no separate slug; the rail
 *                                id doubles as the "See All" route param)
 *   rail.title                -> category.name, category.display_name
 *   rail.display               -> category.style   (best-effort — see
 *                                 RAIL_DISPLAY_TO_STYLE; CategoryRail's style
 *                                 vocabulary — 'top-number-poster',
 *                                 'continue-watching', etc. — has no 1:1 CMS
 *                                 equivalent, so hero/billboard/spotlight/grid
 *                                 fall back to the default carousel rendering)
 *   rail.options                -> category.custom_fields (raw passthrough)
 *   (none)                       -> category.logo, category.wallpaper
 *                                    (CMS rails don't carry branding assets;
 *                                    left undefined, same as most legacy
 *                                    categories without a logo)
 *
 * ---------------------------------------------------------------------
 * FIELD MAPPING — CHANNEL (rail item -> legacy `channel`)
 * ---------------------------------------------------------------------
 * The CMS home API does NOT normalize rail items to one shape — the shape
 * depends on `rail.source` (see `hydrate()` in apps/api/src/home/resolve.ts).
 * This mapper handles the shapes we know about and falls back to a
 * best-effort passthrough otherwise:
 *
 *   dsp_catalog / recommended / because_you_watched  (raw content_items rows)
 *     id              -> _id
 *     title            -> title
 *     slug              -> slug
 *     type               -> programming_type ('series' -> 'Series', else raw),
 *                            content_type (raw)
 *     image_asset_id      -> poster (2x3 rendition), spotlight_poster (16x9
 *                            rendition) via GET /img/:id/:aspect/:size — see
 *                            KNOWN GAP #2 below
 *     gigcaster_id          -> gigcaster_id (keeps ChannelCard's LIVE-badge /
 *                              CMSService.resolveGigcasterIdsForChannel path
 *                              working unchanged)
 *     duration_sec           -> duration_sec (passthrough)
 *
 *   bkfc_events / live_now  (raw events rows)
 *     id -> _id, name -> title, slug -> slug, event_date -> event_date
 *     (event_date needs no rename — channelDateUtils.getChannelEventDateValue
 *     already reads `channel.event_date` directly)
 *
 *   continue_watching / watchlist
 *     content_id / position_sec / duration_sec / (added_at | updated_at) only
 *     — see KNOWN GAP #1.
 *
 * ---------------------------------------------------------------------
 * KNOWN GAPS (flagged for the next slice / backend follow-up, not papered
 * over here):
 * ---------------------------------------------------------------------
 *   1. `continue_watching` / `watchlist` rail items come back as bare
 *      { content_id, position_sec/added_at, duration_sec } — the CMS API
 *      does not join content_items, so there is no title/image to show.
 *      This mapper emits a `_unhydrated: true`, titleless/imageless channel
 *      rather than fabricating data; ChannelCard will render a placeholder
 *      until either the backend joins content_items or a client-side lookup
 *      is added.
 *   2. ChannelCard.js preserves whatever host/path the API gives it for
 *      `poster`/`spotlight_poster` (it only appends the existing sizing
 *      grammar, e.g. `/320/480?webp=1`); for a bare id it builds the URL
 *      from the API-supplied image CDN host (`cacheService.getImageCdnUrl()`,
 *      sourced from `/init/config` — there is no build-time fallback). This service
 *      still populates the fields correctly, so CMS image URLs render as
 *      given rather than being mangled by a Cachefly-specific rewrite.
 */
import { CMS_API_BASE_URL } from '../../config/backendEndpoints';

const DEFAULT_HOME_SLUG = 'home';

// CMS `rail.display` -> legacy CategoryRail `category.style`. Anything not
// listed here (hero/billboard/spotlight/grid) has no legacy equivalent yet
// and intentionally maps to `undefined` (CategoryRail's default carousel).
const RAIL_DISPLAY_TO_STYLE = {
  top10: 'top-number-poster',
  progress: 'continue-watching',
};

function imageRenditionUrl(imageAssetId, aspect, width) {
  if (!imageAssetId || !CMS_API_BASE_URL) {
    return null;
  }
  return `${CMS_API_BASE_URL}/img/${encodeURIComponent(imageAssetId)}/${aspect}/${width}.webp`;
}

/** dsp_catalog / recommended / because_you_watched / bkfc_events / live_now */
function mapContentLikeItem(item) {
  const title = item.title || item.name || 'Untitled';
  return {
    _id: item.id,
    title,
    slug: item.slug || null,
    programming_type: item.type === 'series' ? 'Series' : item.type || undefined,
    content_type: item.type || undefined,
    poster: imageRenditionUrl(item.image_asset_id, '2x3', 480),
    spotlight_poster: imageRenditionUrl(item.image_asset_id, '16x9', 720),
    gigcaster_id: item.gigcaster_id || undefined,
    duration_sec: item.duration_sec,
    event_date: item.event_date,
    is_live: item.is_live,
    fullCmsItem: item,
  };
}

/** continue_watching / watchlist — see KNOWN GAP #1 above. */
function mapUnhydratedResumeItem(item) {
  const hasProgress = item.position_sec != null;
  return {
    _id: item.content_id,
    title: null,
    _unhydrated: true,
    resumption_data: hasProgress
      ? {
          progress:
            item.duration_sec > 0
              ? Math.min(100, Math.round((item.position_sec / item.duration_sec) * 100))
              : 0,
          point: item.position_sec,
          duration: item.duration_sec,
        }
      : undefined,
    fullCmsItem: item,
  };
}

function mapRailItem(item) {
  if (!item || typeof item !== 'object') {
    return null;
  }
  if (item.content_id && !item.id && !item.title && !item.name) {
    return mapUnhydratedResumeItem(item);
  }
  return mapContentLikeItem(item);
}

function mapRailToCategory(rail) {
  return {
    _id: rail.id,
    name: rail.title || 'Untitled',
    display_name: rail.title || 'Untitled',
    channels: Array.isArray(rail.items) ? rail.items.map(mapRailItem).filter(Boolean) : [],
    slug: rail.id,
    style: RAIL_DISPLAY_TO_STYLE[rail.display],
    logo: undefined,
    wallpaper: undefined,
    custom_fields: rail.options || undefined,
  };
}

/** /app/load's `homepage` entries are already flat ({ name, channels }, no
 *  nested `category` object), unlike /v1/home/:slug's `rails`. */
function mapHomepageEntryToCategory(entry, index) {
  return {
    _id: entry.is_upcoming_events ? 'upcoming-live-events' : `cms-app-load-${index}`,
    name: entry.name || 'Untitled',
    display_name: entry.name || 'Untitled',
    channels: Array.isArray(entry.channels) ? entry.channels.map(mapRailItem).filter(Boolean) : [],
    slug: undefined,
    style: entry.is_upcoming_events ? 'top-number-poster' : undefined,
    logo: undefined,
    wallpaper: undefined,
    custom_fields: undefined,
  };
}

async function parseJsonSafe(response) {
  try {
    return await response.json();
  } catch (_error) {
    return null;
  }
}

class CmsHomeService {
  /**
   * GET /v1/home/:slug — the personalized home screen.
   * @param {{slug?: string}} [opts]
   * @returns {Promise<{categories: object[], raw: object}>}
   */
  async getHome({ slug = DEFAULT_HOME_SLUG } = {}) {
    const response = await fetch(`${CMS_API_BASE_URL}/v1/home/${encodeURIComponent(slug)}`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      const body = await parseJsonSafe(response);
      const error = new Error(
        body?.error || `CmsHomeService: GET /v1/home/${slug} failed (${response.status})`,
      );
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    const categories = Array.isArray(data.rails) ? data.rails.map(mapRailToCategory) : [];

    return { categories, raw: data };
  }

  /**
   * GET /app/load — anonymous bootstrap payload. Combines the legacy-shaped
   * `homepage` array with the CMS `rails` (mapped identically to getHome()),
   * de-duped by category id so a slug that appears in both isn't rendered
   * twice.
   * @returns {Promise<{categories: object[], raw: object}>}
   */
  async getAppLoadBootstrap() {
    const response = await fetch(`${CMS_API_BASE_URL}/app/load`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      const body = await parseJsonSafe(response);
      const error = new Error(
        body?.error || `CmsHomeService: GET /app/load failed (${response.status})`,
      );
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    const homepageCategories = Array.isArray(data.homepage)
      ? data.homepage.map(mapHomepageEntryToCategory)
      : [];
    const railCategories = Array.isArray(data.rails) ? data.rails.map(mapRailToCategory) : [];

    const seenIds = new Set(homepageCategories.map(c => c._id));
    const categories = [...homepageCategories, ...railCategories.filter(c => !seenIds.has(c._id))];

    return { categories, raw: data };
  }
}

const cmsHomeService = new CmsHomeService();

export default cmsHomeService;
export { CmsHomeService, mapRailToCategory, mapHomepageEntryToCategory, mapRailItem };
